import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  ArrowRight,
  BarChart3,
  Layers,
  PoundSterling,
  FileText,
  SlidersHorizontal,
  BookOpen,
} from "lucide-react";
import { SeoPageFooter } from "@/components/seo-page-footer";

const PAGE_URL = "https://thepropertool.com/construction-estimating-software";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Construction Estimating Software",
  "description": "Construction estimating software for contractors. Quantity-based cost build-ups with UK labour and materials rates, trade-category breakdowns, and labour/materials/plant splits.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Construction Estimating Software", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is construction estimating software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Construction estimating software automates the process of building a cost estimate for a construction project. It typically combines a database of labour, materials, and plant rates with a structured quantity takeoff process to produce an itemised estimate. More advanced tools like The Proper Tool use AI to generate the estimate from a project description, drawings, or photos, reducing the time to produce an accurate cost build-up from hours to minutes.",
      },
    },
    {
      "@type": "Question",
      "name": "Can The Proper Tool do quantity takeoffs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool generates quantity-based estimates: each line item carries a quantity, unit (m², m, nr, etc.) and unit rate. The AI derives quantities from your project description or uploaded drawings. For example, it extracts floor area from architect's plans to calculate screed and floor insulation quantities automatically.",
      },
    },
    {
      "@type": "Question",
      "name": "Does it use BCIS rates?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool uses its own 31-category UK construction cost library (462 rated tasks), calibrated to current UK residential market rates. Rates are not sourced from BCIS directly but are comparable to BCIS Residential Building Cost data for UK residential projects. You can override any rate in the library with your own figures, and the tool will use your rates on future estimates.",
      },
    },
    {
      "@type": "Question",
      "name": "Is The Proper Tool suitable for both residential and commercial construction?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool is optimised for UK residential construction: extensions, conversions, renovations, and general building works. Its cost library, trade categories, and AI model are all calibrated for residential projects. For large-scale commercial or civil engineering work, a specialist commercial estimating platform would be more appropriate.",
      },
    },
    {
      "@type": "Question",
      "name": "How does construction estimating software compare to manual Excel estimating?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Manual Excel estimating typically takes 2–4 hours per quote, requires maintaining your own rate database, is prone to formula errors and missed items, and produces inconsistent formatting. Dedicated estimating software like The Proper Tool reduces this to under 10 minutes per quote, maintains rates automatically, flags scope gaps, and outputs a professionally formatted PDF. At a billing rate of £40–£60/hour, the time saving alone covers the software cost within the first quote.",
      },
    },
  ],
};

const FEATURES = [
  {
    icon: Layers,
    title: "Quantity-based cost build-ups",
    description: "Each line item carries a quantity, unit, and unit rate. The AI derives quantities from your project description or drawings: floor area, linear metres, item counts.",
  },
  {
    icon: PoundSterling,
    title: "UK labour and materials rates",
    description: "462 rated tasks across 31 trade categories with current UK market rates. Override any rate with your own figure and your rate always takes priority.",
  },
  {
    icon: SlidersHorizontal,
    title: "Labour / materials / plant split",
    description: "Every line item carries a labour, materials, and plant cost breakdown. Set per-category rules so your estimates always reflect the right split for your business.",
  },
  {
    icon: BarChart3,
    title: "Trade-category breakdowns",
    description: "31 categories: groundworks, steelwork, carpentry, plastering, roofing, M&E, drainage, landscaping, and more. Totals grouped by trade section.",
  },
  {
    icon: FileText,
    title: "Reads drawings and plans",
    description: "Upload PDFs of architect's drawings or structural plans. The tool extracts beam sizes, spans, and floor areas to improve estimate accuracy.",
  },
  {
    icon: BookOpen,
    title: "Scope completeness check",
    description: "Built-in expert validation flags missing dependencies: skip hire for demolition, tanking for a wet room, fire protection for exposed steelwork.",
  },
];

const SPREADSHEET_VS_SOFTWARE = [
  { aspect: "Time per estimate", spreadsheet: "2–4 hours", software: "Under 10 minutes" },
  { aspect: "Rate database", spreadsheet: "Manual upkeep", software: "Auto-maintained, 462 tasks" },
  { aspect: "Missed items", spreadsheet: "Common", software: "Flagged by AI validation" },
  { aspect: "Quantity errors", spreadsheet: "Formula-dependent", software: "AI-derived from description/drawings" },
  { aspect: "PDF output", spreadsheet: "Manual formatting", software: "Auto-generated, branded" },
  { aspect: "Cost breakdown (L/M/P)", spreadsheet: "Manual calculation", software: "Auto-split per category" },
];

const FAQS = [
  {
    q: "What is construction estimating software?",
    a: "Construction estimating software automates the process of building a cost estimate for a construction project. It typically combines a database of labour, materials, and plant rates with a structured quantity takeoff process to produce an itemised estimate. More advanced tools like The Proper Tool use AI to generate the estimate from a project description, drawings, or photos, reducing the time to produce an accurate cost build-up from hours to minutes.",
  },
  {
    q: "Can The Proper Tool do quantity takeoffs?",
    a: "Yes. The Proper Tool generates quantity-based estimates: each line item carries a quantity, unit (m², m, nr, etc.) and unit rate. The AI derives quantities from your project description or uploaded drawings. For example, it extracts floor area from architect's plans to calculate screed and floor insulation quantities automatically.",
  },
  {
    q: "Does it use BCIS rates?",
    a: "The Proper Tool uses its own 31-category UK construction cost library (462 rated tasks), calibrated to current UK residential market rates. Rates are not sourced from BCIS directly but are comparable to BCIS Residential Building Cost data for UK residential projects. You can override any rate in the library with your own figures, and the tool will use your rates on future estimates.",
  },
  {
    q: "Is The Proper Tool suitable for both residential and commercial construction?",
    a: "The Proper Tool is optimised for UK residential construction: extensions, conversions, renovations, and general building works. Its cost library, trade categories, and AI model are all calibrated for residential projects. For large-scale commercial or civil engineering work, a specialist commercial estimating platform would be more appropriate.",
  },
  {
    q: "How does construction estimating software compare to manual Excel estimating?",
    a: "Manual Excel estimating typically takes 2–4 hours per quote, requires maintaining your own rate database, is prone to formula errors and missed items, and produces inconsistent formatting. Dedicated estimating software like The Proper Tool reduces this to under 10 minutes per quote, maintains rates automatically, flags scope gaps, and outputs a professionally formatted PDF. At a billing rate of £40–£60/hour, the time saving alone covers the software cost within the first quote.",
  },
];

export default function ConstructionEstimatingSoftwarePage() {

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Construction Estimating Software | The Proper Tool"
        description="Construction estimating software for contractors. Quantity-based cost build-ups with UK labour, materials and plant rates across 31 trade categories. From £29/mo, 14-day free trial."
        canonical="/construction-estimating-software"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Construction Estimating Software</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Construction Estimating Software for Contractors
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Accurate cost build-ups with quantity-based estimates, UK labour and materials rates, and labour/materials/plant splits across 31 trade categories. Produced in under 10 minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/comparisons">See alternatives</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · No credit card required</p>
          </div>
        </section>

        {/* What good estimating software should do */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">What good construction estimating software does</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A proper estimating tool covers direct works, <Link href="/glossary#preliminaries" className="text-primary underline underline-offset-2 hover:text-primary/80">preliminaries</Link>, and overhead. It handles the complexity that causes under-pricing.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((f) => (
                <div key={f.title} className="bg-muted/30 rounded-xl border border-border p-6">
                  <f.icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-semibold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature walkthrough */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center">From description to estimate in three steps</h2>
            <p className="text-lg text-muted-foreground mb-12 text-center max-w-2xl mx-auto">
              No complex spreadsheet setup. No rate-database maintenance. Just describe the job and get a full cost build-up.
            </p>
            <div className="space-y-8">
              {[
                {
                  step: "01",
                  title: "Input: drawings, description, or photos",
                  body: "Upload architect's drawings, structural plans, site photos, or type a plain-text job description. The Proper Tool reads all three, extracting beam sizes, floor areas, and dimensions from drawings to improve quantity accuracy.",
                },
                {
                  step: "02",
                  title: "AI generates a fully itemised estimate",
                  body: "The AI produces a quantity-based cost build-up: every trade, every line item, with quantities (m², m, nr), unit rates from the UK cost library, and a labour/materials/plant cost split per item. All 31 trade categories are considered.",
                },
                {
                  step: "03",
                  title: "Review, adjust, and export",
                  body: "Review every line: edit quantities, swap rates, add or remove items. Apply or dismiss expert validation flags. Export a professionally formatted PDF with your company branding, full scope of works, and cost totals.",
                },
              ].map((s) => (
                <div key={s.step} className="flex gap-6 items-start">
                  <div className="text-4xl font-bold text-primary/20 flex-shrink-0 w-12">{s.step}</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Spreadsheet vs software comparison */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Manual Excel estimating vs The Proper Tool</h2>
              <p className="text-lg text-muted-foreground">The time and error cost of spreadsheet estimating adds up quickly.</p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Aspect</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">Manual Excel</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {SPREADSHEET_VS_SOFTWARE.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.aspect}</td>
                      <td className="px-5 py-4 text-sm text-muted-foreground flex items-start gap-2">
                        <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                        {row.spreadsheet}
                      </td>
                      <td className="px-5 py-4 text-sm text-green-600 dark:text-green-400 font-medium">
                        <span className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          {row.software}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/blog/what-to-include-in-builders-quote", label: "What to Include in a Builder's Quote" },
                { href: "/blog/ai-quoting-software-uk-builders", label: "AI Quoting Software for Builders: What It Does and Whether It's Worth It" },
                { href: "/quoting-app-for-builders", label: "Quoting App for Builders" },
                { href: "/estimating-software-for-builders", label: "Estimating Software for Builders" },
                { href: "/job-quoting-software", label: "Job Quoting Software" },
                { href: "/quoting-software-for-contractors", label: "Quoting Software for Contractors" },
                { href: "/free-trial/", label: "Free Trial" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Stop estimating in spreadsheets</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The Proper Tool generates a fully itemised, quantity-based construction estimate in under 10 minutes, with UK rates, L/M/P splits, and expert scope validation built in.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start your free 14-day trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">Plans from £29/mo · No credit card required</p>
          </div>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
